import { observer } from 'mobx-react';
import * as React from 'react';

import { AbacusControl, AbacusExperiment, AbacusVariant } from 'bernie-abacus';
import { ClickTracker } from 'bernie-analytics';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';
import { ViewLarge, Viewport, ViewSmall } from 'bernie-view';

import { CollapseHeight } from 'constants/collapse-height';
import { FilterHeaderWeight } from 'constants/filter-header-config';
import { FilterModel, FilterOptionModel } from 'components/common/search-form/fields/filter-types';

import { UitkExpandoPeek } from 'uitk-react-expando';
import { UitkLayoutGrid, UitkLayoutGridColumnRow, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkRadio, UitkRadioGroup } from 'uitk-react-switch';
import { UitkText } from 'uitk-react-text';

import { FormFieldBinding } from 'components/common/search-form/fields/form-field-binding';
import { SearchFormFieldProps } from 'components/common/search-form/index';

const RadioButtonTracked = ClickTracker(UitkRadio);

export type LocalizedRadioGroupProps = LocalizedComponentProps & SearchFormFieldProps & RadioGroupProps;

export interface RadioGroupProps {
  headerSize: any;
  rfrrSuffix?: () => void;
}

export interface RadioGroupState {
  viewExpanded: boolean;
}

@withLocalization
@FormFieldBinding
@observer
export class RadioGroup extends React.Component<LocalizedRadioGroupProps, RadioGroupState> {
  constructor(props) {
    super(props);

    this.state = { viewExpanded: false };

    this.handleChange = this.handleChange.bind(this);
    this.handleToggleClick = this.handleToggleClick.bind(this);
  }

  private handleToggleClick() {
    this.setState(prevState => ({ viewExpanded: !prevState.viewExpanded }));
  }

  private handleChange(evt) {
    const target = evt.target;
    const { value } = target;

    const { model, modelKey, sortAndFilterOptions, updateModel } = this.props;

    const radioGroup: FilterModel = sortAndFilterOptions && sortAndFilterOptions[modelKey];
    const radioGroupName: string = radioGroup && radioGroup.name;
    const modelValue = model && model[radioGroupName];

    if (!(typeof updateModel === 'function') || typeof modelValue === 'undefined') {
      return;
    }

    updateModel({
      target: {
        name: radioGroupName,
        value
      }
    });
  }

  public render() {
    const { l10n, model, modelKey, sortAndFilterOptions, headerSize, rfrrSuffix } = this.props;
    const { viewExpanded } = this.state;

    const { formatText } = l10n;

    const radioGroup = sortAndFilterOptions && sortAndFilterOptions[modelKey];
    const radioGroupOptions = radioGroup && radioGroup.options;
    const hiddenThreshold = radioGroup && radioGroup.hiddenThreshold;
    const collapseHeight = hiddenThreshold && hiddenThreshold === 5 ? CollapseHeight.FIVE_HEIGHT : CollapseHeight.TEN_HEIGHT;
    const radioGroupName: string = radioGroup && radioGroup.name;

    if (!radioGroupOptions || radioGroupOptions.length === 0) {
      return null;
    }

    return (
      <fieldset className="all-t-margin-six">
        <legend id={`${radioGroupName}-legend`} className="filter-title">
          <UitkText size={headerSize} weight={FilterHeaderWeight.BOLD}>
            {radioGroup.label}
          </UitkText>
        </legend>
        {hiddenThreshold && radioGroupOptions.length > hiddenThreshold ? (
          <UitkExpandoPeek
            className="pwa-switch-group"
            collapseHeight={collapseHeight}
            collapseLabel={formatText('contentToggle.collapse.label')}
            expandLabel={formatText('contentToggle.expand.label')}
            handleClick={this.handleToggleClick}
            isVisible={viewExpanded}
          >
            <Radios
              handleChange={this.handleChange}
              model={model}
              modelKey={modelKey}
              sortAndFilterOptions={sortAndFilterOptions}
              rfrrSuffix={rfrrSuffix}
            />
          </UitkExpandoPeek>
        ) : (
          <div className="pwa-switch-group">
            <Radios
              handleChange={this.handleChange}
              model={model}
              modelKey={modelKey}
              sortAndFilterOptions={sortAndFilterOptions}
              rfrrSuffix={rfrrSuffix}
            />
          </div>
        )}
      </fieldset>
    );
  }
}

const Radios = observer(({ handleChange, model, modelKey, sortAndFilterOptions, rfrrSuffix }) => {
  const singleColumnNames = ['guestRating'];
  const radioGroup: FilterModel = sortAndFilterOptions && sortAndFilterOptions[modelKey];
  const radioGroupOptions: FilterOptionModel[] = radioGroup && radioGroup.options;
  const radioGroupName: string = radioGroup && radioGroup.name;

  const value = (model && model[radioGroupName]) || '';

  let hiddenField;
  if (value) {
    if (!radioGroupOptions.some(option => option.value === value)) {
      hiddenField = <input type="radio" hidden={true} name={radioGroupName} checked={true} value={value} />;
    }
  }

  const getRadioButtons = (columns: UitkLayoutGridColumnRow) => (
    <UitkRadioGroup group={radioGroupName} onChange={handleChange} selected={value}>
      <UitkLayoutGrid columns={columns}>
        {radioGroupOptions.map((option, idx) => (
          <UitkLayoutGridItem>
            <div>
              <RadioButtonTracked
                description={option.description}
                events="click"
                id={`radio-${radioGroupName}-${option.value}`}
                key={`${radioGroupName}-${idx}-${option.value}`}
                label={option.label}
                linkName={`${radioGroupName}.${option.value}`}
                name={radioGroupName}
                rfrr={`${radioGroupName}.${option.value}${rfrrSuffix(option.value)}`}
                value={option.value}
                data-stid={`radio-${radioGroupName}-${option.value}`}
                group={radioGroup.label}
                onChange={handleChange}
                checked={value === option.value}
              />
            </div>
          </UitkLayoutGridItem>
        ))}
      </UitkLayoutGrid>
    </UitkRadioGroup>
  );

  return (
    <React.Fragment>
      <AbacusExperiment name="PWA_JPSmallCrafts_HoldOut">
        <AbacusControl>{getRadioButtons(1)}</AbacusControl>
        <AbacusVariant bucket={1}>{getRadioButtons(1)}</AbacusVariant>
        <AbacusVariant bucket={2}>
          <AbacusExperiment name="PWA_JPSmallCrafts_TwoColumnLayout">
            <AbacusControl>{getRadioButtons(1)}</AbacusControl>
            <AbacusVariant bucket={1}>
              <Viewport>
                <ViewSmall>{getRadioButtons(singleColumnNames.includes(radioGroupName) ? 1 : 2)}</ViewSmall>
                <ViewLarge>{getRadioButtons(1)}</ViewLarge>
              </Viewport>
            </AbacusVariant>
          </AbacusExperiment>
        </AbacusVariant>
      </AbacusExperiment>
      {hiddenField}
    </React.Fragment>
  );
});
